import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Phone, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import Disclosure from "@/components/Disclosure";
import { http, formatApiErrorDetail } from "@/lib/api";
import { BUSINESS_PHONE, BUSINESS_PHONE_FORMATTED, OWNER_FIRST_NAME } from "@/lib/constants";
import { useT } from "@/lib/i18n";

export default function OpportunityPage() {
    const { t, lang } = useT();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone: "", zip_code: "77002",
        notes: "", consent: false,
    });
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = form.first_name && form.last_name && form.email && form.phone && form.consent;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                customer_type: "Other",
                service_of_interest: "Business Opportunity",
                category: "opportunity",
                lang,
                zip_code: form.zip_code || "00000",
                situation: "Interested in the business opportunity",
                first_name: form.first_name,
                last_name: form.last_name,
                phone: form.phone,
                email: form.email.toLowerCase(),
                preferred_contact: "phone",
                consent: !!form.consent,
                notes: form.notes || null,
                source: "opportunity-page",
            };
            const { data } = await http.post("/leads", payload);
            toast.success("Got it! Jeffrey will reach out.");
            navigate(`/thank-you?id=${data.id}&service=Business+Opportunity&channel=opportunity&name=${encodeURIComponent(form.first_name + " " + form.last_name)}&phone=${form.phone}&email=${form.email}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen" data-testid="opportunity-page">
            <Navbar />
            <section className="relative">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="hero-glow -top-20 -left-20" />
                    <div className="absolute inset-0 spark-pattern opacity-20" />
                </div>
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6">
                    <span className="chip reveal"><Briefcase className="w-3 h-3" /> Business opportunity</span>
                    <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white reveal d1">
                        Interested in doing <span className="accent-gradient-text">what I do?</span>
                    </h1>
                    <p className="mt-4 text-lg text-slate-300 max-w-2xl reveal d2">
                        I'm an independent representative. If you've thought about building a side
                        income by helping neighbors save on essentials — same way I do — let's talk.
                        No pressure. No sales pitch here. Just an honest 15-min conversation.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3 reveal d3">
                        <a href={`tel:${BUSINESS_PHONE}`} className="btn-ghost" data-testid="opp-call-cta">
                            <Phone className="w-4 h-4" /> Call {BUSINESS_PHONE_FORMATTED}
                        </a>
                        <a href={`sms:${BUSINESS_PHONE}?&body=Hi ${OWNER_FIRST_NAME}, I'm curious about the business.`} className="btn-primary" data-testid="opp-text-cta">
                            <MessageSquare className="w-4 h-4" /> Text me about the business
                        </a>
                    </div>
                </div>
            </section>

            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <form onSubmit={submit} className="card-surface p-6 sm:p-8" data-testid="opp-form">
                    <h2 className="text-xl font-semibold text-white">Request a 15-min conversation</h2>
                    <p className="text-sm text-slate-400 mt-1">
                        Separate from the plan-comparison form — this stays private and only {OWNER_FIRST_NAME} will see it.
                    </p>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                        <div>
                            <label className="text-xs text-slate-400">First name</label>
                            <input className="input-dark mt-1" data-testid="opp-first-name" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400">Last name</label>
                            <input className="input-dark mt-1" data-testid="opp-last-name" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400">Email</label>
                            <input type="email" className="input-dark mt-1" data-testid="opp-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400">Phone</label>
                            <input className="input-dark mt-1" data-testid="opp-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-xs text-slate-400">Anything I should know? (optional)</label>
                            <textarea className="input-dark mt-1 min-h-[80px]" data-testid="opp-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. background, availability, what interests you..." />
                        </div>
                    </div>
                    <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="opp-consent-row">
                        <input type="checkbox" className="mt-1 w-5 h-5 accent-emerald-500" data-testid="opp-consent" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} />
                        <span className="text-sm text-slate-300 leading-relaxed">
                            I agree to be contacted by {OWNER_FIRST_NAME} by phone, text, or email about the business opportunity.
                        </span>
                    </label>
                    <button type="submit" disabled={!canSubmit || submitting} className="btn-primary mt-6 disabled:opacity-50" data-testid="opp-submit">
                        {submitting ? "Sending..." : "Send request"}
                        {!submitting && <CheckCircle2 className="w-4 h-4" />}
                    </button>
                </form>
                <div className="mt-6"><Disclosure /></div>
            </section>
            <Footer />
            <StickyMobileCTA />
        </div>
    );
}
